"use client";

import { usePasswordValidation } from "@/components/hooks/usePasswordValidation";
import axios from "axios";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import {
  decodeRecoveryToken,
  editPasswordRequest,
  mapEditPasswordError,
  mapTokenValidationError,
  validateRecoveryToken,
} from "../Services/editPasswordService";
import type { AlertData, TokenStatus } from "../Types/types";

export const useEditPassword = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const hasDParam = Boolean(searchParams.get("d"));

  const [email, setEmail] = useState<string | null>(null);
  const [tipoId, setTipoId] = useState<string | null>(null);
  const [numId, setNumId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [errorAlertData, setErrorAlertData] = useState<AlertData | null>(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successAlertData, setSuccessAlertData] = useState<AlertData | null>(
    null
  );

  const {
    passwordError,
    confirmPasswordError,
    handlePasswordChange,
    handleConfirmPasswordChange,
  } = usePasswordValidation();

  const [showSplash, setShowSplash] = useState(hasDParam);
  const handleSplashFinish = () => setShowSplash(false);

  const [tokenStatus, setTokenStatus] = useState<TokenStatus>("idle");

  // validar parámetros al montar
  useEffect(() => {
    // No validar nada mientras esté el splash
    if (showSplash) return;

    const encoded = searchParams.get("d");
    if (!encoded) {
      setErrorAlertData({
        title: "Acceso denegado",
        message: "El enlace de recuperación no contiene información válida.",
      });
      return;
    }

    try {
      const decoded = decodeRecoveryToken(encoded);

      setEmail(decoded.email);
      setTipoId(decoded.tipo_id);
      setNumId(decoded.num_id);
      setToken(decoded.token);

      setTokenStatus("validating");

      validateRecoveryToken(decoded)
        .then(() => {
          setTokenStatus("valid");
        })
        .catch((err) => {
          setTokenStatus("invalid");
          setErrorAlertData(mapTokenValidationError(err?.response?.status));
        });
    } catch {
      setTokenStatus("invalid");
      setErrorAlertData({
        title: "Enlace inválido",
        message: "El enlace de recuperación no es válido.",
      });
    }
  }, [searchParams, showSplash]);

  const handlePasswordInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    handlePasswordChange(value);
    // Si ya hay algo en confirmPassword, revalidar la coincidencia
    if (confirmPassword) {
      handleConfirmPasswordChange(value, confirmPassword);
    }
  };

  const handleConfirmPasswordInputChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);
    handleConfirmPasswordChange(password, value);
  };

  const isFormValid =
    !!password && !!confirmPassword && !passwordError && !confirmPasswordError;

  const handleCloseErrorAlert = () => {
    // no limpiamos el estado de token → queda bloqueado
    setErrorAlertData(null);
    setTimeout(() => {
      router.push("/login");
    }, 300);
  };

  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(false);
    setSuccessAlertData(null);
    setTimeout(() => {
      router.push("/login");
    }, 300);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !email || !tipoId || !numId || !token) return;

    try {
      setLoading(true);
      setApiError(null);
      setSuccessMessage(null);

      const data = await editPasswordRequest({
        email,
        tipo_id: tipoId,
        num_id: numId,
        password,
        password_confirmation: confirmPassword,
        token,
      });

      if (data.success) {
        setSuccessAlertData({
          title: "¡Contraseña actualizada!",
          message:
            data.message ||
            "Tu contraseña fue actualizada correctamente. Serás redirigido al login para que puedas iniciar sesión con tu nueva contraseña.",
        });
        setShowSuccessAlert(true);
        setSuccessMessage(
          data.message || "Tu contraseña fue actualizada correctamente"
        );
      } else {
        setApiError(data.error || "Error al actualizar la contraseña");
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setApiError(
          mapEditPasswordError(err.response?.status, err.response?.data?.error)
        );
      } else {
        setApiError("Error del servidor. Intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    password,
    confirmPassword,
    passwordError,
    confirmPasswordError,
    onPasswordChange: handlePasswordInputChange,
    onConfirmPasswordChange: handleConfirmPasswordInputChange,
    isFormValid,
    loading,
    apiError,
    successMessage,
    tokenStatus,
    errorAlertData,
    showSuccessAlert,
    successAlertData,
    showSplash,
    handleSplashFinish,
    onSubmit: handleSubmit,
    onCloseErrorAlert: handleCloseErrorAlert,
    onCloseSuccessAlert: handleCloseSuccessAlert,
  };
};
